import { useEffect, useState } from 'react'
import { WIDE, atLeast } from '../breakpoints.js'

/**
 * Ширина окна для кода (D52).
 *
 * Раскладку делает CSS, и это правило: разметка одна, различие живёт
 * в разделе «Широкие экраны» файла `app.css`. Сюда вынесено только
 * то, что CSS сделать не может, — а именно решения о том, что вообще
 * отрисовывать. Таких два, и оба про экономию, а не про вид:
 *
 *   - `useWide` — показывать ли содержимое всех четырёх вкладок листа.
 *     Спрятать лишние стилем нельзя: вкладка заклинаний при отрисовке
 *     спрашивает у сервера список класса, и на телефоне этот запрос
 *     уходил бы впустую с каждой вкладки;
 *   - `useUltra` — открывать ли панель роллера сразу. Она становится
 *     закреплённой колонкой только с `--rc-bp-ultra`; открывать её
 *     раньше значит накрыть лист панелью во всю ширину.
 *
 * `matchMedia` берётся у окна прямо здесь, а не свойством сверху.
 * Причина не в лени: свойство пришлось бы протащить через шесть
 * уровней ради одного логического значения, а окружения, где окна
 * нет, у клиента ровно два — тест в jsdom и серверная отрисовка,
 * которой у нас нет. Оба честно получают `false`: узкая раскладка —
 * основная (D10), и ошибиться в её сторону безопасно.
 */
function useAtLeast(width: number): boolean {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    // jsdom `matchMedia` не реализует вовсе — не «возвращает пусто»,
    // а не имеет метода. Проверка не про старые браузеры, а про тесты.
    const media = window.matchMedia?.(atLeast(width))
    if (media === undefined) return
    setMatches(media.matches)
    const listen = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }
    media.addEventListener('change', listen)
    return () => {
      media.removeEventListener('change', listen)
    }
  }, [width])

  return matches
}

/** Две колонки: содержимое нескольких вкладок помещается рядом,
 *  а панель роллера встаёт сбоку. */
export function useWide(): boolean {
  return useAtLeast(WIDE)
}
